import { useState } from "react";
import { Link, Outlet, useLocation } from "react-router";
import { AppShell, type NavItem, type NavLinkRenderProps } from "@bruntworkwear-apps/ui";
import { navItemsFor } from "@/app/navigation";
import { FEEDBACK_NAV_HREF, FeedbackReporter } from "@/modules/feedback";
import { useAppConfig } from "@/modules/overview";

/**
 * Binds the design system's router-agnostic AppShell to React Router.
 *
 * The shell takes a `renderLink` callback precisely so `@bruntworkwear-apps/ui`
 * does not depend on a router; this is where that callback is supplied. It is
 * also what lets one entry in the sidebar be an action rather than a
 * destination — see {@link FEEDBACK_NAV_HREF}.
 *
 * The report dialog's open flag lives here rather than in the feedback module,
 * because the trigger and the dialog end up on opposite sides of the shell: the
 * trigger is rendered by `renderLink` inside the sidebar, and the dialog belongs
 * beside the outlet. One owner is simpler than a context for a single boolean.
 */
export function AppLayout() {
  const { pathname } = useLocation();
  const { data } = useAppConfig();
  const [isReporting, setIsReporting] = useState(false);

  // The report entry is appended only when the API says a report can go
  // somewhere. That decision, and why it is defensive about a config it does not
  // fully recognize, lives in navItemsFor.
  const navItems = navItemsFor(data);

  const renderNavEntry = (item: NavItem, { className, onNavigate }: NavLinkRenderProps) => {
    if (item.href === FEEDBACK_NAV_HREF) {
      return (
        <button
          type="button"
          className={className}
          onClick={() => {
            // Closes the mobile drawer as a real navigation would, so the dialog
            // does not open behind it.
            onNavigate();
            setIsReporting(true);
          }}
        >
          {item.icon}
          {item.label}
        </button>
      );
    }

    return (
      <Link to={item.href} className={className} onClick={onNavigate}>
        {item.icon}
        {item.label}
      </Link>
    );
  };

  return (
    <AppShell
      appName="Accounting Inventory"
      navItems={navItems}
      currentPath={pathname}
      userEmail={data?.user_email ?? null}
      renderLink={renderNavEntry}
    >
      <Outlet />
      <FeedbackReporter isOpen={isReporting} onClose={() => setIsReporting(false)} />
    </AppShell>
  );
}
